import { CSSProperties } from 'react';
import { Controller, Control, FieldValues } from 'react-hook-form';
import { Stack, OutlinedInput, Button } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

import TextFormLabel from '../text/TextFormLabel';
import TextFormError from '../text/TextFormError';
import { useLocalization } from '../../../localization';

export interface NumberInputProps<T extends FieldValues = FieldValues> {
  name: string;
  control: Control<T>;
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
  readonly?: boolean;
  min?: number;
  max?: number;
  step?: number;
  allowNegative?: boolean;
  decimalPlaces?: number;
  defaultValue?: number;
  onValueChange?: (value: number | null) => void;
  incrementable?: boolean;
}

export const NumberInputActionField = <T extends FieldValues = FieldValues>(props: NumberInputProps<T>) => {
  const { t } = useLocalization();
  const {
    name,
    control,
    label,
    required = false,
    placeholder,
    disabled = false,
    readonly = false,
    style,
    min,
    max,
    step = 1,
    allowNegative = false,
    decimalPlaces,
    defaultValue = 0,
    onValueChange,
    incrementable = true,
  } = props;

  const formatValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    if (decimalPlaces !== undefined) {
      return value.toFixed(decimalPlaces);
    }
    return value.toString();
  };

  const parseValue = (value: string): number | null => {
    if (value === '') return null;
    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue)) return null;

    if (decimalPlaces !== undefined) {
      return Number(parsedValue.toFixed(decimalPlaces));
    }
    return parsedValue;
  };

  const handleChange = (value: string, onChange: (value: number | null) => void) => {
    // Allow typing minus sign for negative numbers
    if (allowNegative && value === '-') {
      onChange(null);
      onValueChange?.(null);
      return;
    }

    // Allow decimal point
    if (value.endsWith('.')) {
      onChange(parseValue(value.slice(0, -1)));
      onValueChange?.(parseValue(value.slice(0, -1)));
      return;
    }

    const parsedValue = parseValue(value);

    if (value === '') {
      onChange(null);
      onValueChange?.(null);
      return;
    }
    if (parsedValue === null) return;

    onChange(parsedValue);
    onValueChange?.(parsedValue);
  };

  return (
    <Stack spacing={0.5}>
      {label && <TextFormLabel label={label} required={required} />}
      <Controller
        name={name as any}
        control={control}
        defaultValue={defaultValue as any}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const increment = () => {
            const newValue = (value ?? 0) + step;
            onChange(newValue);
            onValueChange?.(newValue);
          };

          const decrement = () => {
            const newValue = (value ?? 0) - step;
            onChange(newValue);
            onValueChange?.(newValue);
          };
          return (
            <Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={decrement}
                  disabled={disabled || (min !== undefined && value <= min)}
                >
                  <RemoveIcon />
                </Button>
                <OutlinedInput
                  value={formatValue(value)}
                  onChange={e => handleChange(e.target.value, onChange)}
                  placeholder={t(placeholder ?? '')}
                  disabled={disabled}
                  error={Boolean(error)}
                  endAdornment={null}
                  inputProps={{
                    min,
                    max,
                    step,
                    readOnly: readonly,
                    style: { textAlign: 'center' },
                  }}
                  style={{ ...style }}
                  sx={{
                    width: '3.5rem',
                  }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={increment}
                  disabled={!incrementable || disabled || (max !== undefined && value >= max)}
                >
                  <AddIcon />
                </Button>
              </Stack>
              <TextFormError error={error} />
            </Stack>
          );
        }}
      />
    </Stack>
  );
};
